import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Layout from '@/components/Layout'
import AdminDialog from '@/components/AdminDialog'
import { openFromRow } from '@/lib/openFromRow'
import { FilterSelect, CategorySelect } from '@/components/FilterDropdown'
import { billingApi, type Package } from '@/api/billing'
import { errorMessage } from '@/api/client'
import PointBatchPolicyPanel from '@/components/PointBatchPolicyPanel'
import BillingRulePanel from '@/components/BillingRulePanel'
import PackagePreview from '@/components/PackagePreview'
import { discountSummary, isCampaign, packageValidation } from '@/lib/packagePresentation'
import './packages.css'
import { usePackageVisibility } from '@/lib/packageVisibility'
const field = 'mt-1 h-10 w-full rounded-lg border px-3 text-sm'
const blank: Package = {
  category: '常用套餐',
  id: '',
  code: 'TOPUP_',
  name: '',
  description: '',
  priceFen: 1000,
  points: 1000,
  bonusPoints: 0,
  bonusValidDays: null,
  recommended: false,
  active: true,
  sortOrder: 50,
  updatedAt: '',
}
export default function Packages({ prototypeMode = false }: { prototypeMode?: boolean }) {
  const localPreview = import.meta.env.DEV && prototypeMode
  const [demoItems, setDemoItems] = useState<Package[]>([])
  const [previewOpen, setPreviewOpen] = useState(false)
  const [discount, setDiscount] = useState('5')
  const [discountError, setDiscountError] = useState('')
  useEffect(() => {
    if (localPreview) void import('@/testing/packageFixtures').then(m => setDemoItems(m.packageFixtures))
  }, [localPreview])
  const display = usePackageVisibility()
  const visibility = display.status
  const cache = useQueryClient()
  const query = useQuery({
    queryKey: ['billing-packages'],
    queryFn: billingApi.packages,
    enabled: !localPreview,
  })
  const allItems = localPreview ? demoItems : query.data?.items || []
  const visibleItems = display.visible(allItems)
  const [status, setStatus] = useState('')
  const [keyword, setKeyword] = useState('')
  const [edit, setEdit] = useState<Package>()
  const [reason, setReason] = useState('')
  const [notice, setNotice] = useState<{ text: string }>()
  const duplicate = edit && allItems.find(p => p.id !== edit.id && (p.code === edit.code || (isCampaign(p) === isCampaign(edit) && p.name.trim().toLowerCase() === edit.name.trim().toLowerCase())))
  const validation = edit ? packageValidation(edit) : ''
  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(undefined), 3200)
    return () => window.clearTimeout(timer)
  }, [notice])
  const save = useMutation({
    mutationFn: async (p: Package & { reason: string }) => {
      const invalid = packageValidation(p)
      if (invalid) throw new Error(invalid)
      if (!localPreview) return billingApi.savePackage(p)
      const saved = { ...p, id: p.id || crypto.randomUUID(), updatedAt: new Date().toISOString() }
      setDemoItems(items => [...items.filter(item => item.id !== saved.id), saved])
      return saved
    },
    onSuccess: () => {
      setEdit(undefined)
      setNotice({ text: localPreview ? '已保存到本地演示，刷新页面恢复初始数据' : '套餐保存成功' })
      if (!localPreview) void cache.invalidateQueries({ queryKey: ['billing-packages'] })
    },
  })
  const items = allItems.filter(
    (p) =>
      (!status || String(p.active) === status) &&
      (!keyword ||
        `${p.code} ${p.name}`
          .toLowerCase()
          .includes(keyword.trim().toLowerCase())),
  )
  function change(k: keyof Package, value: unknown) {
    setEdit((p) => (p ? { ...p, [k]: value } : p))
  }
  return (
    <Layout preview={localPreview} prototypeMode={localPreview}>
      <div className="package-workspace">
      {localPreview && <p role="status" className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">本地交互预览 · 数据来自仓库套餐迁移示例，尚未核对线上实时配置。所有编辑仅保留在本页内存，不连接线上保存接口。</p>}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">套餐管理</h1>
          <p className="mt-2 text-sm text-slate-500">
            管理实付金额、折扣与赠送规则，核对用户实际看到的套餐。
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
        <button type="button" className="rounded-lg border border-brand-600 bg-white px-4 py-2 text-sm font-medium text-brand-600" onClick={() => setPreviewOpen(true)}>用户端预览</button>
        <button
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white"
          onClick={(e) => {
            openFromRow(e)
            setEdit({ ...blank, hideDuringCampaign: true })
            setReason('')
            save.reset()
          }}
        >
          新增套餐
        </button>
        </div>
      </div>
      <div className="package-metrics">
        <div><span>全部充值套餐</span><strong>{allItems.length}</strong></div>
        <div><span>用户端实际展示</span><strong>{visibleItems.length}</strong></div>
        <div><span>已上架</span><strong className="!text-green-700">{allItems.filter(p => p.active).length}</strong></div>
        <div><span>未上架</span><strong>{allItems.filter(p => !p.active).length}</strong></div>
      </div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p className="max-w-3xl text-sm text-indigo-900">在编辑套餐中设置“活动期间是否隐藏此套餐”。仅活动期间生效，未上架套餐始终不展示。{localPreview ? '当前为本地演示。' : '保存套餐后应用到用户端。'}</p>
      </div>
      {previewOpen && <AdminDialog title="用户端预览" extraWide onClose={() => setPreviewOpen(false)}><PackagePreview items={allItems} localPreview={localPreview} /></AdminDialog>}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-600">
          套餐名称 / 编码
          <input
            className={field}
            placeholder="搜索套餐"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </label>
        <FilterSelect
          label="状态"
          value={status}
          onChange={setStatus}
          options={[
            ['', '全部状态'],
            ['true', `已上架（${allItems.filter(p => p.active).length}）`],
            ['false', `未上架（${allItems.filter(p => !p.active).length}）`],
          ]}
        />
        <button
          className="rounded-lg border px-4 py-2 text-sm"
          onClick={() => {
            setKeyword('')
            setStatus('')
            if (!localPreview) void query.refetch()
          }}
        >
          重置
        </button>
      </div>
      {notice && createPortal(
        <p role="status" className="package-save-notice">
          <span aria-hidden="true" className="package-save-notice-icon">✓</span>
          {notice.text}
        </p>, document.body
      )}
      {query.isError && (
        <p role="alert" className="text-red-700">
          {errorMessage(query.error)}{' '}
          <button onClick={() => void query.refetch()}>重试</button>
        </p>
      )}
      <div className="min-h-64 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[920px] text-left text-sm">
          <thead className="sticky top-0 bg-slate-100 text-slate-600">
            <tr>
              {[
                '套餐 / 编码',
                '上架状态',
                '分类',
                '金额',
                '基础积分',
                '赠送积分',
                '到账积分',
                '折扣 / 优惠',
                '实际展示状态',
                '推荐 / 排序',
                '操作',
                ].map((s) => (
                <th key={s} className="p-3">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr className="border-t" key={p.id}>
                <td className="p-3">
                  <p className="font-medium">{p.name}</p>
                  <p className="mt-1 text-xs text-slate-500">{p.code}</p>
                </td>
                <td className="whitespace-nowrap p-3"><span className={'inline-flex rounded-full border px-3 py-1 text-xs font-semibold ' + (p.active ? 'border-green-200 bg-green-50 text-green-800' : 'border-slate-300 bg-slate-100 text-slate-600')}>{p.active ? '● 已上架' : '○ 未上架'}</span></td>
                <td className="p-3">{p.category || '常用套餐'}</td>
                <td className="p-3">¥ {(p.priceFen / 100).toFixed(2)}</td>
                <td className="p-3">
                  {(p.points - p.bonusPoints).toLocaleString()}
                </td>
                <td className="p-3">{p.bonusPoints.toLocaleString()}</td>
                <td className="p-3 font-medium">{p.points.toLocaleString()}</td>
                <td className="p-3"><p className="font-medium text-indigo-700">{discountSummary(p).effectiveDiscount.toFixed(2)} 折</p><p className="mt-1 text-xs text-slate-500">{discountSummary(p).pointsPerYuan.toFixed(2)} 积分 / 元</p><p className="mt-1 text-xs text-slate-500">{isCampaign(p) ? '5折活动' : p.bonusPoints ? `赠送优惠 ${discountSummary(p).giftDiscountPercent.toFixed(2)}%` : '无赠送'}</p>{packageValidation(p) && <p className="mt-1 text-xs text-red-700">规则待校正</p>}</td>
                <td className="p-3">
                  <span
                    className={p.active ? 'text-green-700' : 'text-slate-500'}
                  >
                    {visibility(p, allItems)}
                  </span>
                </td>
                <td className="p-3">
                  {p.recommended ? '推荐' : '普通'} · {p.sortOrder}
                </td>
                <td className="p-3">
                  <button
                    className="text-brand-600 hover:underline"
                    onClick={(e) => {
                      openFromRow(e)
                      setEdit({ ...p, hideDuringCampaign: p.hideDuringCampaign ?? !isCampaign(p) })
                      setReason('')
                      save.reset()
                    }}
                  >
                    编辑配置
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!localPreview && query.isPending ? (
          <p className="p-8 text-center text-slate-500">加载中…</p>
        ) : (
          !items.length && (
            <p className="p-8 text-center text-slate-500">暂无匹配套餐</p>
          )
        )}
      </div>
      <p className="mt-4 text-sm text-slate-500">
        共 {items.length} 个套餐 · 用户端推荐优先，再按排序、金额排列 · 折扣按零售价基准折算，赠送优惠比例与用户端一致
      </p>
      {!localPreview && <><PointBatchPolicyPanel /><BillingRulePanel /></>}
      {edit && (
        <AdminDialog
          title={edit.id ? '编辑套餐' : '新增套餐'}
          busy={save.isPending}
          onClose={() => setEdit(undefined)}
        >
          {edit.active && visibility(edit, allItems) === '活动期间隐藏' && <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">此套餐已上架，目前因活动套餐优先而不在用户端展示。名称、金额、积分和上架状态均可正常编辑；可通过下方“活动期间是否隐藏此套餐”修改展示规则。</p>}
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (duplicate || validation) return
              save.mutate({ ...edit, reason })
            }}
          >
            <label className="text-sm">
              套餐名称
              <input
                required
                maxLength={80}
                className={field}
                value={edit.name}
                aria-invalid={!!duplicate && duplicate.name.trim().toLowerCase() === edit.name.trim().toLowerCase()}
                onChange={(e) => change('name', e.target.value)}
              />
              {duplicate && duplicate.name.trim().toLowerCase() === edit.name.trim().toLowerCase() && <span className="mt-1 block text-xs text-red-600">该套餐名称已存在，请勿重复添加</span>}
            </label>
            <label className="text-sm">
              套餐编码
              <input
                required
                pattern="TOPUP_[A-Z0-9_]{1,48}"
                disabled={!!edit.id}
                className={field}
                value={edit.code}
                aria-invalid={!!duplicate && duplicate.code === edit.code}
                onChange={(e) => change('code', e.target.value)}
              />
              {duplicate && duplicate.code === edit.code && <span className="mt-1 block text-xs text-red-600">该套餐编码已存在，请勿重复添加</span>}
            </label>
            <CategorySelect value={edit.category || ''} options={[...new Set(allItems.map(p=>p.category?.trim() || '常用套餐'))]} onChange={value=>change('category',value)}/>
            <div className="package-discount-editor sm:col-span-2">
              <h3 className="font-semibold text-indigo-950">折扣与到账设置</h3>
              <p className="mt-2 text-xs text-slate-600">零售价基准：1 元 = 100 积分。当前到账积分零售价 ¥{(edit.points / 100).toFixed(2)}，实付 ¥{(edit.priceFen / 100).toFixed(2)}，相当于 {discountSummary(edit).effectiveDiscount.toFixed(2)} 折。</p>
              <div className="mt-3 flex flex-wrap items-end gap-3"><label className="text-sm">目标折扣（折）<input className={field} type="number" min="0.01" max="10" step="0.01" value={discount} onChange={e => { setDiscount(e.target.value); setDiscountError('') }} /></label>
                <button type="button" className="rounded-lg border bg-white px-3 py-2 text-sm" onClick={() => {
                  const n = Number(discount)
                  if (!Number.isFinite(n) || n <= 0 || n > 10) { setDiscountError('请输入大于0且不超过10的折扣'); return }
                  const fen = Math.round(edit.points * n / 10)
                  if (fen < 1) { setDiscountError('折后金额不能低于0.01元'); return }
                  setEdit({ ...edit, priceFen: fen }); setDiscountError('')
                }}>保留到账积分，计算实付</button>
                {isCampaign(edit) && <button type="button" className="rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white" onClick={() => setEdit({ ...edit, points: edit.priceFen * 2, bonusPoints: 0, bonusValidDays: null })}>按实付校正5折到账</button>}
              </div>
              {discountError && <p role="alert" className="mt-2 text-sm text-red-700">{discountError}</p>}
              <p className="mt-3 text-xs text-slate-600">计算结果填入下方金额，保存前可继续调整。折扣为金额与积分的计算值；用户端当前仅对现有5折活动显示原价和折扣角标。常规套餐保持现有赠送优惠文案。</p>
            </div>
            <label className="text-sm">
              金额（元）
              <input
                required
                type="number"
                min="0.01"
                max="1000000"
                step="0.01"
                className={field}
                value={edit.priceFen / 100}
                onChange={(e) =>
                  change('priceFen', Math.round(Number(e.target.value) * 100))
                }
              />
            </label>
            <label className="text-sm">
              基础积分
              <input
                required
                type="number"
                min="1"
                max="1000000000"
                className={field}
                value={edit.points - edit.bonusPoints}
                onChange={(e) =>
                  change('points', Number(e.target.value) + edit.bonusPoints)
                }
              />
            </label>
            <label className="text-sm">
              赠送积分
              <input
                required
                type="number"
                min="0"
                max="999999999"
                className={field}
                value={edit.bonusPoints}
                onChange={(e) => {
                  const n = Number(e.target.value)
                  setEdit({
                    ...edit,
                    bonusPoints: n,
                    bonusValidDays: n === 0 ? null : edit.bonusValidDays,
                    points: edit.points - edit.bonusPoints + n,
                  })
                }}
              />
            </label>
            <label className="text-sm">
              排序
              <input
                required
                type="number"
                min="0"
                max="100000"
                className={field}
                value={edit.sortOrder}
                onChange={(e) => change('sortOrder', Number(e.target.value))}
              />
            </label>
            <p className="text-sm text-slate-500">购买和赠送积分均长期有效，不设置到期日。</p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={edit.active}
                onChange={(e) => change('active', e.target.checked)}
              />
              上架状态：{edit.active ? '已上架' : '未上架'}
            </label>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm sm:col-span-2">
              <div className="flex items-center gap-2"><label htmlFor="package-campaign-hidden">活动期间是否隐藏此套餐</label><span className="package-help"><button type="button" aria-label="活动期间隐藏说明" aria-describedby="package-hidden-help">!</button><span id="package-hidden-help" role="tooltip">仅用户端隐藏，后台可正常编辑</span></span></div>
              <select id="package-campaign-hidden" className={field} value={edit.hideDuringCampaign ? 'yes' : 'no'} onChange={e => change('hideDuringCampaign', e.target.value === 'yes')}><option value="yes">是，活动期间隐藏</option><option value="no">否，活动期间仍展示</option></select>
              <span className="mt-2 block text-xs text-slate-500">仅在有已上架活动套餐时生效。非活动期间按上架状态展示；未上架套餐始终不展示。点击保存套餐后生效。</span>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={edit.recommended}
                onChange={(e) => change('recommended', e.target.checked)}
              />
              推荐套餐
            </label>
            <label className="text-sm sm:col-span-2">
              套餐说明
              <textarea
                maxLength={1000}
                className={field + ' h-20 py-2'}
                value={edit.description}
                onChange={(e) => change('description', e.target.value)}
              />
            </label>
            <label className="text-sm sm:col-span-2">
              修改原因
              <textarea
                required
                minLength={2}
                maxLength={500}
                placeholder="填写原因，记录到操作日志"
                className={field + ' h-20 py-2'}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <p className="text-sm text-slate-500 sm:col-span-2">
              合计到账 {edit.points.toLocaleString()}{' '}
              积分。下架后不可新购，已有订单保留原金额及积分。
            </p>
            {save.isError && (
              <p role="alert" className="text-sm text-red-700 sm:col-span-2">
                {errorMessage(save.error)}
              </p>
            )}
            {validation && <p role="alert" className="text-sm text-red-700 sm:col-span-2">{validation}</p>}
            <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600 sm:col-span-2">保存后状态：<strong>{edit.active ? '已上架' : '未上架'}</strong> · 用户端：{!edit.active ? '不展示（未上架）' : edit.hideDuringCampaign && [...allItems.filter(p => p.id !== edit.id), edit].some(p => p.active && isCampaign(p)) ? '活动期间隐藏' : '用户端展示'}。保存后可从页面右上角查看用户端预览。</p>
            {duplicate && <p role="alert" className="text-sm text-red-700 sm:col-span-2">该套餐名称或编码已存在，请编辑已有套餐，不要重复添加。</p>}
            <button
              disabled={save.isPending || !!duplicate || !!validation}
              className="justify-self-end rounded-lg bg-brand-600 px-5 py-2 text-white disabled:cursor-not-allowed disabled:opacity-40 sm:col-span-2"
            >
              {save.isPending ? '保存中…' : '保存套餐'}
            </button>
          </form>
        </AdminDialog>
      )}
      </div>
    </Layout>
  )
}
